import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const CreateKittyEnrollment = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    plan_id: '',
    emi_amount: '',
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadPlans();
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await axiosInstance.get('/kitty/plans');
      setPlans(res.data);
    } catch (err) {
      console.error('Error loading plans:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        emi_amount: parseFloat(formData.emi_amount),
      };
      await axiosInstance.post('/kitty/enrollments', submitData);
      toast.success('Kitty enrollment created!');
      navigate('/kitty');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creating enrollment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/kitty')} className="p-2" data-testid="back-button">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="page-title" data-testid="create-enrollment-title">Create Kitty Enrollment</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="premium-card p-6">
          <h2 className="text-lg font-semibold mb-4">Enrollment Details</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Customer</label>
              <select
                className="input-field w-full"
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                required
                data-testid="customer-select"
              >
                <option value="">-- Select Customer --</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} - {customer.mobile}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Plan</label>
              <select
                className="input-field w-full"
                value={formData.plan_id}
                onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                required
                data-testid="plan-select"
              >
                <option value="">-- Select Plan --</option>
                {plans.map((plan) => (
                  <option key={plan.plan_id} value={plan.plan_id}>
                    {plan.name} ({plan.tenure_months} months)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">EMI Amount (₹)</label>
              <input
                type="number"
                step="0.01"
                className="input-field w-full"
                value={formData.emi_amount}
                onChange={(e) => setFormData({ ...formData, emi_amount: e.target.value })}
                required
                data-testid="emi-amount-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                className="input-field w-full"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
                data-testid="start-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                className="input-field w-full"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
                data-testid="end-date-input"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/kitty')}
            className="btn-secondary flex-1"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex-1"
            disabled={loading}
            data-testid="submit-enrollment-button"
          >
            {loading ? 'Creating...' : 'Create Enrollment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateKittyEnrollment;
